import { useRollbar } from '@rollbar/react'
import React, { useRef, useState } from 'react'
import Cropper, { ReactCropperElement } from 'react-cropper'
import toast from 'react-hot-toast'
import { useTranslation } from 'next-i18next'
import { PrimaryButton } from 'shared/components/button'
import { BadRequest } from 'shared/errors/bad-request'
import { useUploadImage } from 'shared/hooks/use-upload-image'
import { DataFile } from 'shared/types/datafile-type'
import { FieldErrorAndDescription } from '../../form/field-error-and-description'
import Modal, { ModalProps } from '../../modal'
import { FileUploadErrorResponseInterface } from '../types'
import 'cropperjs/dist/cropper.css'

export type ImageCropperType = Omit<React.ComponentPropsWithoutRef<typeof Cropper>, 'src'>

export type ImageCropperAndUploaderProps = Pick<ModalProps, 'onClose'> & {
  onUploadSuccess: (imageFileData: DataFile) => void
  imageFile: File | undefined
  source?: string
  type?: string
  isOpened: boolean
  setIsOpened: React.Dispatch<React.SetStateAction<boolean>>
  imageCropperProps?: ImageCropperType
  isRounded?: boolean
}

export const ImageCropperAndUploader = ({
  imageCropperProps: imageCropperProps,
  imageFile,
  type,
  source,
  isOpened,
  setIsOpened,
  onClose,
  onUploadSuccess,
  isRounded,
}: ImageCropperAndUploaderProps) => {
  const {
    viewMode = 1,
    autoCropArea = 1,
    aspectRatio = 1,
    checkOrientation = false, // NOTE: it fixes this issue https://github.com/fengyuanchen/cropperjs/issues/671
    background = true,
    dragMode = 'move',
    toggleDragModeOnDblclick = false,
    style = { height: 400, width: '100%' },
    ...cropperPropsRest
  } = imageCropperProps || {}

  const { t } = useTranslation()
  const rollbar = useRollbar()

  const cropperRef = useRef<ReactCropperElement>(null)
  const cropperWrapperRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  const finalPreviewRef = useRef<HTMLDivElement>(null)

  const [uploadError, setUploadError] = useState<string | null>(null)

  const { uploadImage, isFetching } = useUploadImage()

  const hidePreview = () => {
    if (cropperWrapperRef.current) cropperWrapperRef.current.style.opacity = ''

    if (previewRef.current) previewRef.current.style.display = 'block'
    if (finalPreviewRef.current) {
      finalPreviewRef.current.style.zIndex = ''
      finalPreviewRef.current.innerText = ''
    }
  }
  const showPreview = () => {
    //  NOTE: The Cropper resets the cropBox after render because of that we overlay it with the finalPreviewRef.
    // We can't use the previewRef because it's controlled by the Cropper, so it changes after render too.
    if (previewRef.current && finalPreviewRef.current) {
      finalPreviewRef.current.appendChild(previewRef.current.cloneNode(true))
      finalPreviewRef.current.style.zIndex = '1000'
      previewRef.current.style.display = 'none'
    }

    //  NOTE: we use the cropperWrapperRef because applying styles to the cropperRef is buggy
    if (cropperWrapperRef.current) cropperWrapperRef.current.style.opacity = '0'
  }

  const onLoad = async function () {
    try {
      if (typeof cropperRef.current?.cropper !== 'undefined') {
        showPreview()

        // Create a cropped file client-side and upload it
        const originalFile = imageFile as File
        const canvas = cropperRef.current.cropper.getCroppedCanvas()

        if (!canvas) {
          try {
            rollbar.error('Failed to get cropped canvas', {
              source,
              type,
              fileName: imageFile?.name,
              fileSize: imageFile?.size,
            })
          } catch {}
        }

        const blob: Blob = await new Promise((resolve, reject) => {
          const outputType = originalFile?.type || 'image/png'
          canvas.toBlob(
            b => (b ? resolve(b) : reject(new Error(t('image_uploader.crop_failed')))),
            outputType,
          )
        })

        const croppedFile = new File([blob], originalFile?.name || 'image.png', {
          type: blob.type,
          lastModified: Date.now(),
        })

        const imageFileData = await uploadImage({
          file: croppedFile,
          source,
          type,
        })
        onUploadSuccess(imageFileData)
        setIsOpened(false)
      }
    } catch (e) {
      try {
        rollbar.error('Image upload failed', {
          error: e instanceof Error ? { name: e.name, message: e.message, stack: e.stack } : e,
          source,
          type,
          fileName: imageFile?.name,
          fileSize: imageFile?.size,
        })
      } catch {}

      if (e instanceof Error && e?.message && typeof e.message === 'string') {
        const msg = e.message.trim()
        setUploadError(msg)
      } else if (e instanceof BadRequest) {
        const errors = (e as unknown as FileUploadErrorResponseInterface).errors as any

        if (Array.isArray(errors)) {
          setUploadError(errors.join('\n'))
        } else if (errors?.common && Array.isArray(errors.common)) {
          setUploadError(errors.common.join('\n'))
        } else if (errors?.image?.file && Array.isArray(errors.image.file)) {
          const msg = errors.image.file.join('\n')
          setUploadError(msg)
        } else if (errors?.file && Array.isArray(errors.file)) {
          const msg = errors.file.join('\n')
          setUploadError(msg)
        }
      }

      hidePreview()
      toast.error(uploadError || t('global.error'))
    }
  }

  return (
    <Modal
      opened={isOpened}
      onClose={() => {
        setIsOpened(false)
      }}
      afterLeave={() => {
        setUploadError(null)
        onClose()
        hidePreview()
      }}
      title={t('global.upload_image')}
    >
      <div className="relative w-full overflow-hidden">
        <div ref={finalPreviewRef} className="absolute inset-0 flex items-center justify-center" />
        <div
          data-is-rounded={isRounded}
          className="absolute h-[400px] w-full overflow-hidden data-[is-rounded=true]:rounded-[50%]"
          ref={previewRef}
        />
        <div ref={cropperWrapperRef}>
          <Cropper
            className={isRounded ? 'sio-cropper-rounded' : ''}
            ref={cropperRef}
            src={imageFile && URL.createObjectURL(imageFile)}
            preview={(previewRef || undefined) as any} // NOTE: need it to get rid of null (the react-cropper throws an error because of null)
            style={style}
            viewMode={viewMode}
            background={background}
            autoCropArea={autoCropArea}
            aspectRatio={aspectRatio}
            checkOrientation={checkOrientation}
            dragMode={dragMode}
            toggleDragModeOnDblclick={toggleDragModeOnDblclick}
            {...cropperPropsRest}
          />
        </div>
      </div>
      <FieldErrorAndDescription error={uploadError} errorClassName={'text-center mt-2'} />
      <PrimaryButton className="ml-auto mt-2" onClick={onLoad} isLoading={isFetching}>
        {t('global.click_to_upload')}
      </PrimaryButton>
    </Modal>
  )
}
